import React, { useEffect, useState } from 'react';
import { Button, Result, Space } from 'antd';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getDefaultUserRoute } from '../../utils/userRoutes';
import { DefaultAdminPage } from '../../config';
import { normalizeLoginType } from '../../utils/auth';
import {
  isChunkLoadError,
  tryReloadForChunkError,
} from '../../utils/chunkLoadError';

function getErrorMessage(error) {
  if (isRouteErrorResponse(error)) {
    return error.statusText || error.data?.message || `HTTP ${error.status}`;
  }
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

export default function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.data);
  const loginType = normalizeLoginType(user?.loginType);
  const [reloadAttempted, setReloadAttempted] = useState(false);

  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkError) return;
    const didReload = tryReloadForChunkError();
    if (!didReload) {
      setReloadAttempted(true);
    }
  }, [chunkError]);

  const homeRoute =
    loginType === 'admin'
      ? DefaultAdminPage
      : loginType === 'user'
        ? getDefaultUserRoute(user)
        : '/sign-in';

  if (chunkError && !reloadAttempted) {
    return (
      <div
        style={{
          padding: '48px 24px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Result
          status="info"
          title={t('routeError.updatingTitle', 'Updating application…')}
          subTitle={t(
            'routeError.updatingMessage',
            'A newer version is available. Reloading…'
          )}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '48px 24px' }}>
      <Result
        status="error"
        title={
          chunkError
            ? t('routeError.chunkTitle', 'Failed to load page')
            : t('routeError.title', 'Something went wrong')
        }
        subTitle={
          chunkError
            ? t(
                'routeError.chunkMessage',
                'The app may have been updated. Please reload the page.'
              )
            : t(
                'routeError.message',
                'An unexpected error occurred while loading this page.'
              )
        }
        extra={
          <Space wrap>
            <Button type="primary" onClick={() => window.location.reload()}>
              {t('routeError.reload', 'Reload page')}
            </Button>
            <Button onClick={() => navigate(homeRoute)}>
              {t('routeError.back', 'Back to Dashboard')}
            </Button>
          </Space>
        }
      />
      {process.env.NODE_ENV === 'development' ? (
        <pre
          style={{
            maxWidth: 640,
            margin: '0 auto',
            padding: 12,
            background: '#f5f5f5',
            borderRadius: 8,
            fontSize: 12,
            overflow: 'auto',
          }}
        >
          {getErrorMessage(error)}
        </pre>
      ) : null}
    </div>
  );
}
